import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTableCellsLarge, faList, faArrowLeft, faGear, faClock, faTicket, faFilm, faCalendar } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect } from "react";
import * as Dialog from '@radix-ui/react-dialog';

const PreferencesModal = ({ open, onOpenChange, onSearch }) => {
    const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
    const [selectedPasses, setSelectedPasses] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);

    const timeSlots = [
        { id: 'morning', label: '8h-12h', icon: faClock },
        { id: 'afternoon', label: '12h-18h', icon: faClock },
        { id: 'evening', label: '18h-00h', icon: faClock }
    ];

    const passes = [
        { id: 'ugc', label: 'UGC Illimité', icon: faTicket },
        { id: 'pathe', label: 'Pathé', icon: faTicket }
    ];

    const filmTypes = [
        { id: 'new', label: 'Nouveautés', icon: faFilm },
        { id: 'classic', label: 'Classiques', icon: faFilm }
    ];

    const daysOfWeek = [
        { id: 'monday', label: 'Lun', icon: faCalendar },
        { id: 'tuesday', label: 'Mar', icon: faCalendar },
        { id: 'wednesday', label: 'Mer', icon: faCalendar },
        { id: 'thursday', label: 'Jeu', icon: faCalendar },
        { id: 'friday', label: 'Ven', icon: faCalendar },
        { id: 'saturday', label: 'Sam', icon: faCalendar },
        { id: 'sunday', label: 'Dim', icon: faCalendar }
    ];

    const toggleSelection = (set, value) => {
        set(prev => 
            prev.includes(value) 
                ? prev.filter(item => item !== value)
                : [...prev, value]
        );
    };

    const FilterSection = ({ title, items, selectedItems, setSelectedItems, isCompact = false }) => (
        <div className="space-y-3">
            <h3 className="font-medium text-gray-700">{title}</h3>
            <div className={`flex flex-wrap gap-1 ${isCompact ? 'max-w-[300px]' : ''}`}>
                {items.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => toggleSelection(setSelectedItems, item.id)}
                        className={`flex items-center justify-center gap-1 px-2 py-1 rounded-lg transition-colors ${
                            selectedItems.includes(item.id)
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        } ${isCompact ? 'w-10' : ''}`}
                    >
                        {isCompact ? (
                            <span>{item.label}</span>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={item.icon} />
                                <span>{item.label}</span>
                            </>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[10000]" />
                <Dialog.Content className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] bg-white rounded-lg p-6 w-[90vw] max-w-[450px] max-h-[85vh] overflow-y-auto z-[10001]">
                    <Dialog.Title className="text-xl font-semibold mb-6">Préférences</Dialog.Title>
                    
                    <div className="space-y-6">
                        <FilterSection
                            title="Jours"
                            items={daysOfWeek}
                            selectedItems={selectedDays}
                            setSelectedItems={setSelectedDays}
                            isCompact={true}
                        />

                        <FilterSection
                            title="Horaires"
                            items={timeSlots}
                            selectedItems={selectedTimeSlots}
                            setSelectedItems={setSelectedTimeSlots}
                        />

                        <FilterSection
                            title="Cartes d'abonnement"
                            items={passes}
                            selectedItems={selectedPasses}
                            setSelectedItems={setSelectedPasses}
                        />

                        <FilterSection
                            title="Type de films"
                            items={filmTypes}
                            selectedItems={selectedTypes}
                            setSelectedItems={setSelectedTypes}
                        />
                    </div>

                    <div className="mt-8 flex justify-end gap-3">
                        <Dialog.Close asChild>
                            <button className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                                Annuler
                            </button>
                        </Dialog.Close>
                        <button 
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            onClick={() => {
                                onSearch({
                                    query: "",
                                    filters: {
                                        days: selectedDays,
                                        timeSlots: selectedTimeSlots,
                                        passes: selectedPasses,
                                        types: selectedTypes
                                    }
                                });
                                onOpenChange(false);
                            }}
                        >
                            Sauvegarder
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const Filter = ({ onViewChange = () => {}, selectedMovie = null, onBack = () => {}, onSearch = () => {} }) => {
    const [activeView, setActiveView] = useState("grid");
    const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

    const handleViewChange = (view) => {
        setActiveView(view);
        onViewChange(view);
    };

    return (
        <div className="flex items-center mb-5">
            <button
                onClick={() => setIsPreferencesOpen(true)}
                className="bg-gray-100 rounded-lg px-4 py-2 hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
                <FontAwesomeIcon icon={faGear} className="text-gray-700" />
                <span className="text-gray-700 font-medium">Préférences</span>
            </button>

            <PreferencesModal 
                open={isPreferencesOpen} 
                onOpenChange={setIsPreferencesOpen}
                onSearch={onSearch}
            />

            <div className="ml-auto flex items-center">
                {selectedMovie ? (
                    // Show back arrow if a movie is selected
                    <button
                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        onClick={onBack}
                        aria-label="Retour"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="text-gray-700" />
                    </button>
                ) : (
                    // Show grid/list toggle buttons
                    <div className="bg-gray-100 rounded-lg flex">
                        <button
                            className={`p-2 rounded-l-lg transition-colors ${activeView === "list" ? "bg-white shadow-sm" : "bg-transparent hover:bg-gray-200"}`}
                            onClick={() => handleViewChange("grid")}
                            aria-label="Vue grille"
                        >
                            <FontAwesomeIcon
                                icon={faTableCellsLarge}
                                className={`${activeView === "grid" ? "text-gray-800" : "text-gray-500"}`}
                            />
                        </button>

                        <button
                            className={`p-2 rounded-r-lg transition-colors ${activeView === "grid" ? "bg-white shadow-sm" : "bg-transparent hover:bg-gray-200"}`}
                            onClick={() => handleViewChange("list")}
                            aria-label="Vue liste"
                        >
                            <FontAwesomeIcon
                                icon={faList}
                                className={`${activeView === "list" ? "text-gray-800" : "text-gray-500"}`}
                            />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Filter;
